export const VIDEO_REFERENCE_RECENT_GROUP_LIMIT = 10;

export interface VideoReferenceRunViewCounts {
  queued: number;
  active: number;
  ready: number;
  failed: number;
  otherTerminal: number;
}

export interface VideoReferenceRunViewGroup<T = unknown> {
  id: string;
  kind: "single" | "batch";
  batchId: string | null;
  createdAt: string | null;
  runs: T[];
  counts: VideoReferenceRunViewCounts;
}

export interface VideoReferenceRunsView<T = unknown> {
  active: VideoReferenceRunViewGroup<T>[];
  recent: VideoReferenceRunViewGroup<T>[];
}

type GroupableRun = {
  id: string;
  batchId?: string | null;
  batchIndex?: number | null;
  status: string;
  createdAt?: string | Date | null;
  updatedAt?: string | Date | null;
  quickEditInProgress?: boolean;
};

function timeOf(value: string | Date | null | undefined): number {
  const time = value ? new Date(value).getTime() : 0;
  return Number.isFinite(time) ? time : 0;
}

/**
 * Group only the batch identity already stored on a run. This intentionally
 * does not infer batches from matching products, timestamps, or other fields.
 */
export function groupVideoReferenceRuns<T extends GroupableRun>(
  runs: T[],
  isActive: (run: T) => boolean,
): VideoReferenceRunsView<T> {
  const groups = new Map<string, { batchId: string | null; runs: T[] }>();

  for (const run of runs) {
    const batchId = typeof run.batchId === "string" && run.batchId.trim()
      ? run.batchId
      : null;
    const id = batchId ? `batch:${batchId}` : `run:${run.id}`;
    const existing = groups.get(id);
    if (existing) existing.runs.push(run);
    else groups.set(id, { batchId, runs: [run] });
  }

  const makeGroup = ({ batchId, runs: groupRuns }: { batchId: string | null; runs: T[] }): VideoReferenceRunViewGroup<T> => {
    const orderedRuns = [...groupRuns].sort((a, b) =>
      (a.batchIndex ?? Number.MAX_SAFE_INTEGER) - (b.batchIndex ?? Number.MAX_SAFE_INTEGER) ||
      timeOf(a.createdAt) - timeOf(b.createdAt) ||
      a.id.localeCompare(b.id),
    );
    const counts = orderedRuns.reduce<VideoReferenceRunViewCounts>((result, run) => {
      if (run.status === "queued") result.queued += 1;
      else if (isActive(run)) result.active += 1;
      else if (run.status === "ready") result.ready += 1;
      else if (run.status === "failed") result.failed += 1;
      else result.otherTerminal += 1;
      return result;
    }, { queued: 0, active: 0, ready: 0, failed: 0, otherTerminal: 0 });
    const first = orderedRuns[0];
    return {
      id: batchId ? `batch:${batchId}` : `run:${first.id}`,
      kind: batchId ? "batch" : "single",
      batchId,
      createdAt: first?.createdAt ? new Date(first.createdAt).toISOString() : null,
      runs: orderedRuns,
      counts,
    };
  };

  const active: VideoReferenceRunViewGroup<T>[] = [];
  const recent: VideoReferenceRunViewGroup<T>[] = [];
  for (const group of groups.values()) {
    const shaped = makeGroup(group);
    if (group.runs.some(isActive)) active.push(shaped);
    else recent.push(shaped);
  }

  active.sort((a, b) => timeOf(a.createdAt) - timeOf(b.createdAt) || a.id.localeCompare(b.id));
  recent.sort((a, b) => {
    const aTime = Math.max(...a.runs.map((run) => timeOf(run.updatedAt ?? run.createdAt)));
    const bTime = Math.max(...b.runs.map((run) => timeOf(run.updatedAt ?? run.createdAt)));
    return bTime - aTime || b.id.localeCompare(a.id);
  });
  return { active, recent };
}